/* eslint-disable camelcase */
import { ReactNode } from 'react';

import {
  CONDITION_TYPE_TO_LEFT_COL_TEXT,
  GOAL_TYPES,
} from 'modules/predictiveGoals/components/queryBuilder/constants';

export type Nullable<BaseType> = BaseType | null;

export type ClausePropertyComparison = {
  value?: string;
  comparator: string;
};

export type GoalCondition = {
  query: ClausePropertyComparison;
  queryKey: string;
};

export type GoalQueryClause = {
  dataType?: string;
  name: string;
  type: keyof typeof GOAL_TYPES | string;
  window?: string;
};

export type GoalQueryState = {
  eventConditions?: Array<GoalCondition>;
} & GoalQueryClause;

export type PredictiveGoalQueryClause = {
  property_comparison?: ClausePropertyComparison;
  occurrence_comparison?: ClausePropertyComparison;
} & GoalQueryClause;

export type ReadableCriteriaElement = {
  render: () => ReactNode;
};

export type PredictiveOrClause = PredictiveGoalQueryClause;

export type QueryBuilderOrClause = PredictiveGoalQueryClause & GoalQueryState;

export type PredictiveGoalQuery = {
  and_clauses: {
    or_clauses: PredictiveOrClause[];
  }[];
};

export type PredictiveGoalDate = {
  timestamp: string;
  timezone: string;
};

export enum ModelQuality {
  Weak = 'Weak',
  Satisfactory = 'Satisfactory',
  Strong = 'Strong',
}

export enum FeatureType {
  CUSTOMEVENT = 'CustomEvent',
  PROPERTY = 'Property',
  SYSTEMEVENT = 'SystemEvent',
  PURCHASEEVENT = 'PurchaseEvent',
}

export enum Direction {
  DECREASE = 'Decrease',
  INCREASE = 'Increase',
  INCONCLUSIVE = 'Inconclusive',
}

export type ConstituentFeature = {
  name: string;
  weight: number;
  __typename: 'ConstituentFeature';
};

export type Feature = {
  name: string;
  strength: number;
  direction: Direction;
  featureType: FeatureType;
  constituentFeatures?: ConstituentFeature[];
  __typename: 'Feature';
};

export type ExplainableFeatures = {
  features: Nullable<Array<Feature>>;
};

export type PredictiveGoalModelDetails = {
  model_quality: ModelQuality;
  explainableFeatures?: ExplainableFeatures;
  previous_model_quality: Nullable<ModelQuality>;
  num_users: number;
  lift_at_each_percentile: Array<number>;
  prob_at_each_percentile: Array<number>;
};

export type GoalValidation = {
  is_valid: boolean;
  error_msg: string;
};

export const PredictiveGoalType = 'PredictiveAudienceGoal';

export type PredictiveAudienceGoal = {
  createdAt: PredictiveGoalDate;
  createdBy: string;
  description: string;
  goal: PredictiveGoalQuery;
  goalValidation: Nullable<GoalValidation>;
  id: number;
  lastScoredAt: Nullable<PredictiveGoalDate>;
  lastTrainedAt: Nullable<PredictiveGoalDate>;
  modelDetails: Nullable<PredictiveGoalModelDetails>;
  name: string;
  status: GoalStatus;
  updatedAt: PredictiveGoalDate;
  updatedBy: string;
  userProfileField: string;
  __typename: typeof PredictiveGoalType;
};

export enum PredictiveGoalsTableColValues {
  name = 'name',
  userProfileField = 'userProfileField',
  status = 'status',
  updatedAt = 'updatedAt',
  modelDetails = 'modelDetails',
  lastTrainedAt = 'lastTrainedAt',
}

export type NoGoalsEmptyStateProps = {
  Icon: () => JSX.Element;
  title: string;
  subtitle?: string;
};

export type InProgressStatusProps = {
  goal: PredictiveAudienceGoal;
};

export type ResultsPageProps = {
  goal: PredictiveAudienceGoal;
  onMutation?: (mutatedGoal: PredictiveAudienceGoal) => void;
};

export type RefreshingCaptionProps = {
  goalStatus: GoalStatus;
};

export type DistributionChartWithRadioSelectorsProps = {
  goal: PredictiveAudienceGoal;
};

export type PredictiveGoalsTableProps = {
  loading: boolean;
  rows: PredictiveAudienceGoal[];
  error: boolean;
  onMutation: () => void;
  showPlaceholderContent?: boolean;
};

export type StrengthMeterIconProps = {
  strength: string;
};

export type DeletingStatusProps = {
  goal: PredictiveAudienceGoal;
};

export type StatusPageGoalCriteriaProps = {
  disableDuration?: boolean;
  goal: PredictiveAudienceGoal;
};

export type GoalDetailsPredictionSectionProps = {
  children: ReactNode;
};

export type VocabularyResponse = {
  predictiveAudiencesVocabulary: Nullable<PredictiveAudiencesVocabulary>;
};

export type PredictiveGoalsResponse = {
  predictiveAudienceGoals: Array<PredictiveAudienceGoal>;
};

export type CreatePredictiveGoalVariables = {
  name: string;
  userProfileField: string;
  goal: PredictiveGoalQuery;
  description: string;
  listIds: number[];
};
export type EditPredictiveGoalVariables = {
  id: number;
  name: string;
  userProfileField: string;
  goal: PredictiveGoalQuery;
  description: string;
  listIds: number[];
};
export type CreatePredictiveGoalResponse = {
  predictiveAudienceGoalCreate: PredictiveAudienceGoal;
};
export type ReadPredictiveGoalResponse = {
  predictiveAudienceGoal: PredictiveAudienceGoal;
};
export type EditPredictiveGoalResponse = {
  predictiveAudienceGoalUpdate: PredictiveAudienceGoal;
};
export type DeletePredictiveGoalResponse = {
  predictiveAudienceGoalDelete: PredictiveAudienceGoal;
};
export type PreviewUsersResponse = {
  total_count: number;
};

export type DistributionGraphProps = {
  goalScores: Record<string, number>;
  min: number;
  max: number;
};

export type GoalDuration = {
  id: string;
  label: string;
  value: string;
};

export type KeyedConditionClause = ClausePropertyComparison & {
  key: keyof typeof CONDITION_TYPE_TO_LEFT_COL_TEXT;
};

/**
 @description Goal status mapping which contains six status categories mapped from possible data source statuses. This logical mapping happens on the back end service.
 Preparing = [ValidationQueued, ValidationStarted, ValidationCompleted, TrainingQueued, TrainingStarted, TrainingCompleted, ScoringQueued, ScoringStarted, ScoringCompleted]
 Ready = [Scored]
 Needs Review = [NeedsCustomerReview]
 System Error = [ValidationNeedsEngReview, TrainingNeedsEngReview, ScoringNeedsEngReview]
 Refreshing = [(Preparing && (lastScoredAt not null))]
 Deleting = [DeletingQueued, DeletingStarted, DeletingComplete]
 
 In future work, Archived/Deprecated = [Deleted]
 */
export enum GoalStatus {
  PREPARING = 'Preparing',
  READY = 'Ready',
  NEEDS_REVIEW = 'NeedsReview',
  SYSTEM_ERROR = 'SystemError',
  REFRESHING = 'Refreshing',
  DELETING = 'Deleting',
}

export type GoalStatusPredicate = {
  (goal: PredictiveAudienceGoal): boolean;
};

/** Vocabulary Types */
export enum VocabEvents {
  CUSTOM = 'custom',
  SYSTEM = 'system',
  PURCHASE = 'purchase',
  USER = 'user',
}
export type VocabularyEvent = {
  __typename: string;
  dataType?: string;
  deltaName: string;
  displayName: string;
  type: VocabEvents.CUSTOM | VocabEvents.SYSTEM | VocabEvents.PURCHASE;
  userCount13Week: number;
  userCount1Month: number;
};

export type CustomEvent = {
  __typename: 'CustomEvent';
  dataType: string;
} & VocabularyEvent;

export type PurchaseEvent = {
  __typename: 'PurchaseEvent';
  occurrencesDataType: string;
  purchaseAmountDataType: string;
} & VocabularyEvent;

export type SystemEvent = {
  __typename: 'SystemEvent';
  dataType: string;
} & VocabularyEvent;

export type VocabularyUserFeature = {
  dataType: string;
  deltaName: string;
  displayName: string;
  lastUsed: { timestamp: string; timezone: string };
  type: VocabEvents.USER;
  userCountMostRecent: number;
  values: string[];
  fieldName?: string;
  __typename: 'UserFeature';
};

export type Event = VocabularyEvent | VocabularyUserFeature;

export type VocabularyDuration = {
  durations: string[];
  defaultDuration: string;
  __typename: 'VocabDuration';
};

export type PredictiveAudiencesVocabulary = {
  customEvents: CustomEvent[];
  purchaseEvents: PurchaseEvent[];
  systemEvents: SystemEvent[];
  userFeatures?: VocabularyUserFeature[];
  durations?: Array<VocabularyDuration>;
  computedFor: string;
  __typename?: 'Vocabulary';
};

export type Durations = 'day' | 'week' | 'month';

export type User = {
  id: string;
  fullName: string;
  avatarUrl: string;
};

export type UserAvatarCell = User & {
  emailAddress: string;
};

export type GoalMenuOption = {
  id: string;
  children: ReactNode;
  isDivider?: boolean;
};

export type PreviousStrengthStringProps = {
  currentStrength: Nullable<string>;
  previousStrength: Nullable<string>;
  lastTrainedDate: Nullable<string>;
};

export type SegmentationParams = {
  id: number;
  name: string;
  min: number;
  max: number;
  scoreType: 'probability' | 'percentile';
};

/** Template Types */
export enum InternalTemplates {
  CHURN = 'churn',
  PURCHASE = 'purchase',
  ENGAGE = 'engage',
}

export type UserSelectedInternalTemplate = InternalTemplates | '';

export type TemplateQueryEvent = {
  type: VocabEvents;
  deltaName: string;
  score: number;
};

export type TemplateQuery = {
  template_type: InternalTemplates;
  events: TemplateQueryEvent[];
};

export enum TemplateInternalDeltaNames {
  CHURN = 'unsubscribe',
  PURCHASE = 'purchase',
  ENGAGE = 'email_click',
}

export type InternalTemplateGoalQueriesMap = Record<InternalTemplates, PredictiveGoalQuery>;

export type TemplateEventPopoverContent = {
  action: string;
  eventName: TemplateInternalDeltaNames | '';
  result: string;
};

/**
 * @description Query builder types
 */
export type EventOption = {
  id: string;
  label: string;
  value: string;
  type: string;
  Suffix: string; // used for select dropdown to indicate type of event
  OverflowContent?: ReactNode; // use for select dropdown additional usage hover
};

export type PropertyValue = {
  id: string;
  label: string;
  value: string;
};

export type DurationOption = {
  id: string;
  label: string;
  value: string;
};

export type PropertyOption = {
  id: string;
  label: string;
  value: string;
  Suffix: string;
  values: PropertyValue[];
  dataType: string;
  OverflowContent?: ReactNode;
  lastRefreshDate?: string;
};

export enum VocabularyDataTypes {
  boolean = 'boolean',
  string = 'string',
  long = 'long',
  integer = 'integer',
  double = 'double',
}
